import { useEffect, useState } from "react";
import { useReadContracts } from "wagmi";
import { erc20Abi, formatUnits, type ContractFunctionParameters } from "viem";
import { ADDRESSES, PROTOCOL } from "@/lib/config";

const DEAD = "0x000000000000000000000000000000000000dEaD" as const;
// ETH/USD price feed (Chainlink-compatible aggregator, 8 decimals) on
// Robinhood Chain. No canonical feed address is published for the chain yet,
// so it comes from an optional env var; USD figures stay 0 until one is set.
const ETH_USD_FEED = import.meta.env.VITE_ETH_USD_FEED as `0x${string}` | undefined;

const PAIR_ABI = [
    { type: "function", name: "getReserves", stateMutability: "view", inputs: [],
      outputs: [{ type: "uint112" }, { type: "uint112" }, { type: "uint32" }] },
    { type: "function", name: "token0", stateMutability: "view", inputs: [], outputs: [{ type: "address" }] },
] as const;
const FEED_ABI = [
    { type: "function", name: "latestRoundData", stateMutability: "view", inputs: [],
      outputs: [{ type: "uint80" }, { type: "int256" }, { type: "uint256" }, { type: "uint256" }, { type: "uint80" }] },
] as const;

export interface TokenStats {
    totalSupply: number;
    circulating: number;
    burned: number;
    burnedPct: number;
    holders: number;
    liquidityUsd: number;
    priceUsd: number;
    marketCapUsd: number;
    volume24hUsd: number;
}

const EMPTY: TokenStats = {
    totalSupply: PROTOCOL.totalSupply, circulating: PROTOCOL.totalSupply, burned: 0, burnedPct: 0,
    holders: 0, liquidityUsd: 0, priceUsd: 0, marketCapUsd: 0, volume24hUsd: 0,
};

/// All numbers are real: supply/burn/circulating from the token on-chain,
/// price + liquidity + 24h volume from Dexscreener's live USD quote for the
/// PIGGY/WETH pair, and market cap computed locally as circulating × price.
/// Falls back to on-chain reserves × an optional ETH/USD feed if Dexscreener
/// is unavailable. No hardcoded/demo figures.
export const useTokenStats = (): { stats: TokenStats; live: boolean } => {
    const token = ADDRESSES.PIGGY_TOKEN;
    const pair = ADDRESSES.PIGGY_PAIR;

    // The feed entry is conditional, so the array can't stay a fixed tuple —
    // type it loosely; results below are narrowed individually anyway.
    const contracts: ContractFunctionParameters[] = token && pair
        ? [
              { address: token, abi: erc20Abi, functionName: "totalSupply" },
              { address: token, abi: erc20Abi, functionName: "balanceOf", args: [DEAD] },
              { address: pair, abi: PAIR_ABI, functionName: "getReserves" },
              { address: pair, abi: PAIR_ABI, functionName: "token0" },
              ...(ETH_USD_FEED
                  ? [{ address: ETH_USD_FEED, abi: FEED_ABI, functionName: "latestRoundData" } as const]
                  : []),
          ]
        : [];
    const reads = useReadContracts({
        allowFailure: true,
        contracts,
        query: { enabled: Boolean(token && pair), refetchInterval: 30_000 },
    });

    // Dexscreener is the primary USD source: it already prices the PIGGY/WETH
    // pair in USD (no ETH/USD feed needed, and none is published for this chain
    // yet). We take priceUsd + liquidity + 24h volume from it; market cap we
    // compute ourselves from on-chain circulating supply so it can't drift on
    // a bad circulating-supply guess. Falls back to the on-chain reserve ratio
    // × optional feed below if Dexscreener hasn't indexed a fresh trade.
    const [dex, setDex] = useState<{ priceUsd: number; liquidityUsd: number; volume24hUsd: number } | null>(null);
    useEffect(() => {
        if (!pair) return;
        let cancelled = false;
        const load = () => {
            // Dexscreener chain slug verified 2026-07-11 via their search API:
            // the PIGGY/WETH pair is indexed under chainId "robinhood".
            fetch(`https://api.dexscreener.com/latest/dex/pairs/robinhood/${pair}`)
                .then((r) => r.json())
                .then((d) => {
                    if (cancelled) return;
                    const p = d?.pairs?.[0];
                    if (!p) return;
                    setDex({
                        priceUsd: Number(p.priceUsd ?? 0) || 0,
                        liquidityUsd: Number(p.liquidity?.usd ?? 0) || 0,
                        volume24hUsd: Number(p.volume?.h24 ?? 0) || 0,
                    });
                })
                .catch(() => { /* keep last good value */ });
        };
        load();
        const id = setInterval(load, 30_000);
        return () => { cancelled = true; clearInterval(id); };
    }, [pair]);

    // Real holder count, derived on-chain by the token-holders function.
    // Polls every 60s (with a cache-buster) so new holders show without a
    // reload and aren't pinned to a stale browser/CDN cache entry.
    const [holders, setHolders] = useState(0);
    useEffect(() => {
        let cancelled = false;
        const load = () => {
            fetch(`/.netlify/functions/token-holders?t=${Math.floor(Date.now() / 60_000)}`)
                .then((r) => r.json())
                .then((d) => {
                    if (cancelled) return;
                    const h = Number(d?.holders ?? 0);
                    if (Number.isFinite(h) && h > 0) setHolders(h);
                })
                .catch(() => { /* keep last good value */ });
        };
        load();
        const id = setInterval(load, 60_000);
        return () => { cancelled = true; clearInterval(id); };
    }, []);

    if (!token || !pair) return { stats: EMPTY, live: false };

    // On-chain reads (supply/burn/reserves). These may be momentarily
    // unavailable if the public RPC throttles — in that case we STILL render
    // the Dexscreener-sourced USD metrics below rather than blanking the whole
    // panel. Only supply/burn/circulating depend on these reads.
    const supplyRaw = reads.data?.[0]?.result as bigint | undefined;
    const burnedRaw = reads.data?.[1]?.result as bigint | undefined;
    const reserves = reads.data?.[2]?.result as readonly [bigint, bigint, number] | undefined;
    const token0 = reads.data?.[3]?.result as string | undefined;
    const feed = reads.data?.[4]?.result as readonly [bigint, bigint, bigint, bigint, bigint] | undefined;

    const haveChain = supplyRaw !== undefined && burnedRaw !== undefined;
    // Nothing to show at all yet (first paint, before either source resolves).
    if (!haveChain && !dex) return { stats: EMPTY, live: false };

    const totalSupply = supplyRaw !== undefined ? Number(formatUnits(supplyRaw, 18)) : PROTOCOL.totalSupply;
    const burned = burnedRaw !== undefined ? Number(formatUnits(burnedRaw, 18)) : 0;
    const circulating = totalSupply - burned;

    // On-chain reserve fallback for price/liquidity (only usable with a feed).
    let piggyReserve = 0;
    let wethReserve = 0;
    if (reserves && token0) {
        const piggyIsToken0 = token0.toLowerCase() === token.toLowerCase();
        piggyReserve = Number(formatUnits(piggyIsToken0 ? reserves[0] : reserves[1], 18));
        wethReserve = Number(formatUnits(piggyIsToken0 ? reserves[1] : reserves[0], 18));
    }
    const ethUsd = feed ? Number(feed[1]) / 1e8 : 0;
    const priceFromChain = piggyReserve > 0 ? (wethReserve / piggyReserve) * ethUsd : 0;

    // Primary source for USD figures is Dexscreener; on-chain is only a fallback.
    const priceUsd = dex?.priceUsd || priceFromChain;
    const liquidityUsd =
        dex?.liquidityUsd ||
        (ethUsd ? wethReserve * ethUsd * 2 : piggyReserve * priceUsd * 2);
    // Market cap from on-chain circulating supply × price (not a third party's
    // circulating-supply estimate).
    const marketCapUsd = circulating * priceUsd;
    const volume24hUsd = dex?.volume24hUsd ?? 0;

    return {
        stats: {
            totalSupply, circulating, burned,
            burnedPct: totalSupply > 0 ? (burned / totalSupply) * 100 : 0,
            holders, liquidityUsd, priceUsd, marketCapUsd, volume24hUsd,
        },
        // Live as soon as either real source has data.
        live: Boolean(dex) || haveChain,
    };
};
